import type { Cents, TaxRate } from "../money.js";
import type { PrinterRole } from "./printer.js";
import type { VenueId, MenuCategoryId, MenuItemId } from "../ids.js";

export interface MenuCategory {
  id: MenuCategoryId;
  venueId: VenueId;
  name: string;
  sortOrder: number;
  /** Farbe für schnelle visuelle Orientierung in der Kellner-UI (CSS-Farbe). */
  color?: string;
  /** Standard-Drucker/Station für Artikel dieser Kategorie (z. B. Getränke→Bar). */
  printRole?: PrinterRole;
}

/** Größenvariante (z. B. „0,5l") — beim Bestellen genau eine; Preis ist ABSOLUT. */
export interface SizeVariant {
  label: string;
  priceCents: Cents;
}

/** Beilage/Extra — optionaler Aufpreis (oft 0, kann negativ sein). */
export interface ItemOption {
  label: string;
  priceDeltaCents: Cents;
}

export interface MenuItem {
  id: MenuItemId;
  categoryId: MenuCategoryId;
  name: string;
  /** Kurzes Label für den schnellen Bestell-Button (z. B. "Cola 0,5"). */
  shortLabel?: string;
  priceCents: Cents;
  taxRate: TaxRate;
  /** Button-Farbe in der Kellner-UI. */
  color?: string;
  /** Im Betrieb ausverkauft? Bleibt in der Karte, aber nicht bestellbar. */
  isAvailable: boolean;
  sortOrder: number;
  /** Überschreibt den Drucker/die Station der Kategorie (Ausnahme für diesen Artikel). */
  printRole?: PrinterRole;

  // ---- Verfügbarkeit nach Datum/Uhrzeit (z. B. Schnitzel nur am Sonntag) ----
  /** Nur an diesen Kalendertagen (YYYY-MM-DD) bestellbar; leer/fehlt = jeden Tag. */
  availableDates?: string[];
  /** Nur ab dieser Uhrzeit (HH:MM) bestellbar; leer = ab Tagesbeginn. */
  availableFrom?: string;
  /** Nur bis zu dieser Uhrzeit (HH:MM) bestellbar; leer = bis Tagesende. */
  availableTo?: string;

  // ---- Bestand/Menge (optional): Rest = stock − verkauft (verkauft = bestellte Menge) ----
  /** Gesamtmenge für die Veranstaltung. Fehlt = unbegrenzt. */
  stock?: number;

  // ---- Optionen, direkt am Artikel gepflegt (keine geteilten Gruppen mehr) ----
  /** Größen (z. B. Getränke): genau eine wählbar, Zeilenpreis = Größenpreis (absolut). */
  variants?: SizeVariant[];
  /** Beilage (z. B. Speisen): höchstens EINE wählbar (optional), Aufpreis. */
  beilagen?: ItemOption[];
  /** Extras/Zusätze (z. B. Ketchup/Mayo): MEHRERE wählbar (optional), Aufpreis. */
  extras?: ItemOption[];

  /**
   * Info-/Notiz-Pseudo-Artikel: kein echter Verkauf, dient nur dazu, einen freien Text an
   * eine Station (Küche/Bar/Kassa via printRole) zu schicken. Tippen öffnet direkt das
   * Notiz-Eingabesheet, der Text landet als OrderLine.note und kommt mit dem normalen Bon
   * mit. Preis sollte 0 sein; Auswertungen klammern solche Zeilen aus.
   */
  isInfo?: boolean;
}

/** Soll beim Tippen auf den Artikel zwingend ein Notiz-Text erfasst werden? */
export function requiresNote(item: MenuItem): boolean {
  return item.isInfo === true;
}

/** Effektiver Drucker/Station eines Artikels: Artikel-Override > Kategorie > "kitchen". */
export function effectivePrintRole(item: MenuItem, category: MenuCategory | undefined): PrinterRole {
  return item.printRole ?? category?.printRole ?? "kitchen";
}

/** Hat der Artikel überhaupt Optionen, die beim Bestellen abgefragt werden müssen? */
export function hasOptions(item: MenuItem): boolean {
  return Boolean(item.variants?.length || item.beilagen?.length || item.extras?.length);
}

const pad2 = (n: number) => String(n).padStart(2, "0");
/** Lokales Datum als "YYYY-MM-DD". */
export function localDateStr(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Ist der Artikel zum Zeitpunkt `now` laut Zeitplan bestellbar (ohne isAvailable/Bestand)? */
export function isWithinSchedule(item: MenuItem, now: Date): boolean {
  if (item.availableDates?.length && !item.availableDates.includes(localDateStr(now))) return false;
  const hm = `${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
  if (item.availableFrom && hm < item.availableFrom) return false;
  if (item.availableTo && hm > item.availableTo) return false;
  return true;
}

/** Kurzer Hinweis, wann ein Artikel (zeitlich) verfügbar ist — für die Anzeige. */
export function scheduleLabel(item: MenuItem): string {
  const parts: string[] = [];
  if (item.availableDates?.length) parts.push(item.availableDates.join(", "));
  if (item.availableFrom || item.availableTo) parts.push(`${item.availableFrom ?? ""}–${item.availableTo ?? ""}`);
  return parts.join(" ");
}

/**
 * Vollständiger Speisekarten-Snapshot, wie er ausgeliefert und im PWA-Cache abgelegt wird.
 * `version` steigt bei jeder Änderung — die PWA tauscht nur bei Versionswechsel.
 */
export interface MenuSnapshot {
  venueId: VenueId;
  version: number;
  categories: MenuCategory[];
  items: MenuItem[];
}
